import { createContext, useContext, useState, useCallback, useRef } from 'react'
import Icon from './Icon.jsx'
import { ConfirmModal, NotifyModal } from './Modal.jsx'

/*
 * Single source for user feedback, so screens never reach for browser dialogs.
 *  - toast(message, tone)       -> light, non-blocking (bottom-centre). DESIGN.md §8.
 *  - notify({tone,title,message}) Promise -> important info, single "Compris".
 *  - confirm({...}) -> Promise<boolean> -> ONLY for destructive/irreversible actions.
 */
const FeedbackContext = createContext(null)

let toastSeq = 0

export function FeedbackProvider({ children }) {
  const [toasts, setToasts] = useState([])
  const [confirmState, setConfirmState] = useState(null)
  const [notifyState, setNotifyState] = useState(null)
  const confirmResolve = useRef(null)
  const notifyResolve = useRef(null)

  const toast = useCallback((message, tone = 'default', duration = 3200) => {
    const id = ++toastSeq
    setToasts((t) => [...t, { id, message, tone }])
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), duration)
  }, [])

  const confirm = useCallback((opts) => {
    return new Promise((resolve) => {
      confirmResolve.current = resolve
      setConfirmState({ open: true, ...opts })
    })
  }, [])

  const notify = useCallback((opts) => {
    return new Promise((resolve) => {
      notifyResolve.current = resolve
      setNotifyState({ open: true, ...opts })
    })
  }, [])

  const closeConfirm = (result) => {
    setConfirmState(null)
    confirmResolve.current?.(result)
    confirmResolve.current = null
  }
  const closeNotify = () => {
    setNotifyState(null)
    notifyResolve.current?.()
    notifyResolve.current = null
  }

  return (
    <FeedbackContext.Provider value={{ toast, confirm, notify }}>
      {children}

      {/* Toasts — bottom centre, dark ink (DESIGN.md §8) */}
      <div style={{
        position: 'fixed', bottom: 26, left: 0, right: 0, zIndex: 1100,
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, pointerEvents: 'none',
      }}>
        {toasts.map((t) => {
          const color = { success: '#34D399', error: '#F87171', default: '#fff' }[t.tone] || '#fff'
          const iconName = { success: 'check', error: 'alert-triangle', default: 'info' }[t.tone] || 'info'
          return (
            <div key={t.id} style={{
              display: 'inline-flex', alignItems: 'center', gap: 10,
              background: 'var(--color-ink)', color: '#fff',
              padding: '11px 18px', borderRadius: 12,
              boxShadow: '0 14px 34px -12px rgba(42,26,74,.55)',
              fontSize: 14, fontWeight: 500, animation: 'vtoast .2s ease', pointerEvents: 'auto',
            }}>
              <span style={{ color, display: 'inline-flex' }}><Icon name={iconName} size={17} /></span>
              {t.message}
            </div>
          )
        })}
      </div>

      {confirmState && (
        <ConfirmModal
          {...confirmState}
          onConfirm={() => closeConfirm(true)}
          onCancel={() => closeConfirm(false)}
        />
      )}
      {notifyState && (
        <NotifyModal {...notifyState} onClose={closeNotify} />
      )}
    </FeedbackContext.Provider>
  )
}

export function useFeedback() {
  const ctx = useContext(FeedbackContext)
  if (!ctx) throw new Error('useFeedback must be used within FeedbackProvider')
  return ctx
}
